// src/pages/TeamPage.js

import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { ThemeContext } from '../context/ThemeContext';
import { userService } from '../api';
import SectionTitle from '../components/common/SectionTitle';
import PageLoader from '../components/common/PageLoader';
import ErrorDisplay from '../components/common/ErrorDisplay';

// Styled components
const TeamSection = styled.section`
  padding: 5rem 2rem;
  background: ${props => props.theme.backgroundGradient};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2.5rem;
  margin-top: 3rem;
`;

const TeamCard = styled(motion.div)`
  background: ${props => props.theme.card.background};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: ${props => props.theme.card.shadow};
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.card.hover};

    .card-overlay {
      opacity: 1;
    }
  }
`;

const TeamMemberImage = styled.div`
  width: 100%;
  height: 300px;
  background-image: url(${props => props.src || '/default-avatar.jpg'});
  background-size: contain; 
  background-repeat: no-repeat; 
  background-position: center; 
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 50%;
    background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  }
`;

const CardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
`;

const MemberDetails = styled.div`
  padding: 1.5rem;
`;

const MemberName = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 0.5rem;
  color: ${props => props.theme.text};
  font-weight: 700;
`;

const MemberRole = styled.p`
  color: ${props => props.theme.primary};
  font-weight: 600;
  margin-bottom: 1rem;
  letter-spacing: 0.5px;
`;

const MemberBio = styled.p`
  color: ${props => props.theme.text};
  opacity: 0.8;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SocialLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.text};
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.primary};
    color: white;
    transform: translateY(-3px);
  }
`;

const DetailButton = styled(Link)`
  display: block;
  text-align: center;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  background: ${props => props.theme.primary};
  color: white;
  font-weight: 600;
  text-transform: uppercase;
  text-decoration: none;
  letter-spacing: 1px;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.primaryDark || props.theme.primary};
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const OverlayButton = styled(Link)`
  padding: 0.8rem 2rem;
  background: ${props => props.theme.primary};
  color: white;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  
  &:hover {
    background: white;
    color: ${props => props.theme.primary};
    transform: scale(1.05);
  }
`;

const DepartmentTitle = styled.h2`
  font-size: 1.8rem;
  margin: 3rem 0 1.5rem;
  color: ${props => props.theme.primary};
  position: relative;
  padding-left: 1.2rem;
  font-weight: 700;
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 10%;
    height: 80%;
    width: 5px;
    background: ${props => props.theme.primary};
    border-radius: 4px;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 5rem 2rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
  font-size: 1.2rem;
  background: ${props => props.theme.backgroundSecondary};
  border-radius: 12px;
  margin-top: 2rem;
`;

const TeamPage = () => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Référence pour l'animation avec IntersectionObserver
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  // Charger les membres de l'équipe depuis l'API
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        
        // Récupérer les membres de l'équipe (is_staff=true)
        const response = await userService.getAll({ is_staff: true });
        
        // Vérifier la structure de la réponse
        let members = [];
        if (response.data && Array.isArray(response.data.results)) {
          members = response.data.results;
        } else if (Array.isArray(response.data)) {
          members = response.data;
        }
        
        console.log("Team members data:", members);
        setTeamMembers(members);
        
        // Mise à jour du titre de la page
        document.title = `${t('ourTeam')} | PropentaTech`;
      } catch (err) {
        console.error("Error fetching team members:", err);
        setError(err.response?.data?.detail || t('errorLoadingTeam'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeamMembers();
    
    return () => {
      document.title = 'PropentaTech';
    };
  }, [t]);
  
  // Grouper les membres par département
  const groupByDepartment = (members) => {
    if (!members || members.length === 0) return {};
    
    const departments = {};
    
    members.forEach(member => {
      const dept = member.department || t('otherDepartment');
      if (!departments[dept]) {
        departments[dept] = [];
      }
      departments[dept].push(member);
    });
    
    // Trier les départements par priorité ou par ordre alphabétique
    const sortedDepts = {};
    
    // Définir l'ordre des départements (optionnel)
    const deptOrder = [
      "Management",
      "Direction",
      "Développement",
      "Design",
      "Marketing",
      "Communication",
      "Administratif",
      t('otherDepartment')
    ];
    
    // Ajouter d'abord les départements selon l'ordre défini
    deptOrder.forEach(dept => {
      if (departments[dept]) {
        sortedDepts[dept] = departments[dept];
        delete departments[dept];
      }
    });
    
    // Ajouter les départements restants par ordre alphabétique
    Object.keys(departments).sort().forEach(dept => {
      sortedDepts[dept] = departments[dept];
    });
    
    return sortedDepts;
  };
  
  const departmentMembers = groupByDepartment(teamMembers);
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (error) {
    return <ErrorDisplay error={error} />;
  }
  
  return (
    <TeamSection theme={theme}>
      <Container>
        <SectionTitle
          title={t('ourTeam')}
          subtitle={t('ourTeamSubtitle')}
        />
        
        {teamMembers.length === 0 ? (
          <NoResults theme={theme}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
                <i className="fas fa-users"></i>
              </div>
              <h3>{t('noTeamMembers')}</h3>
              <p>{t('checkBackLater')}</p>
            </motion.div>
          </NoResults>
        ) : (
          <div ref={ref}>
            {Object.entries(departmentMembers).map(([department, members], deptIndex) => (
              <div key={department}>
                <DepartmentTitle theme={theme}>
                  {department}
                </DepartmentTitle>
                
                <TeamGrid>
                  {members.map((member, index) => (
                    <TeamCard
                      key={member.id}
                      theme={theme}
                      initial={{ opacity: 0, y: 30 }}
                      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                      transition={{ duration: 0.5, delay: (deptIndex * 0.2) + (index * 0.1) }}
                    >
                      <TeamMemberImage src={member.profile_picture}>
                        <CardOverlay className="card-overlay">
                          <OverlayButton 
                            to={`/team/${member.id}`} 
                            theme={theme}
                          >
                            {t('viewProfile')}
                          </OverlayButton>
                        </CardOverlay>
                      </TeamMemberImage>
                      <MemberDetails>
                        <MemberName theme={theme}>
                          {member.first_name} {member.last_name}
                        </MemberName>
                        <MemberRole theme={theme}>
                          {member.position || t('teamMember')}
                        </MemberRole>
                        <MemberBio theme={theme}>
                          {member.bio ? 
                            (member.bio.length > 120 ? `${member.bio.substring(0, 120)}...` : member.bio) : 
                            t('noBioAvailable')}
                        </MemberBio>
                        
                        <SocialLinks>
                          {member.linkedin_profile && (
                            <SocialLink
                              href={member.linkedin_profile}
                              target="_blank"
                              rel="noopener noreferrer"
                              theme={theme}
                              aria-label="LinkedIn"
                            >
                              <i className="fab fa-linkedin-in"></i>
                            </SocialLink>
                          )}
                          {member.github_profile && (
                            <SocialLink
                              href={member.github_profile}
                              target="_blank"
                              rel="noopener noreferrer"
                              theme={theme}
                              aria-label="GitHub"
                            >
                              <i className="fab fa-github"></i>
                            </SocialLink>
                          )}
                          {member.twitter_profile && (
                            <SocialLink
                              href={member.twitter_profile}
                              target="_blank"
                              rel="noopener noreferrer"
                              theme={theme}
                              aria-label="Twitter"
                            >
                              <i className="fab fa-twitter"></i>
                            </SocialLink>
                          )}
                          {member.facebook_profile && (
                            <SocialLink
                              href={member.facebook_profile}
                              target="_blank"
                              rel="noopener noreferrer"
                              theme={theme}
                              aria-label="Facebook"
                            >
                              <i className="fab fa-facebook-f"></i>
                            </SocialLink>
                          )}
                        </SocialLinks>
                        
                        <DetailButton 
                          to={`/team/${member.id}`} 
                          theme={theme}
                        >
                          {t('viewDetails')}
                        </DetailButton>
                      </MemberDetails>
                    </TeamCard>
                  ))}
                </TeamGrid>
              </div>
            ))}
          </div>
        )}
      </Container>
    </TeamSection>
  );
};

export default TeamPage;